import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import { userProfileFields } from "../../../constants/forms/userProfileFields";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import {
  setDefaultFieldValues,
  removeEmptyValues,
  normalizeFormErrors,
} from "../../../utils/objectUtils";
import { useAuth } from "../../../hooks/useAuth";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import useNotification from "../../../hooks/useNotification";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const PerfilUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, formatData, setFormData } = useForm(
    setDefaultFieldValues(userProfileFields),
  );

  // --- Validação de Acesso ao Perfil ---
  // Este useEffect garante que o usuário só possa acessar o próprio perfil.
  // Ele é executado antes da query para evitar chamadas desnecessárias.
  useEffect(() => {
    if (user && id && user.id !== Number(id)) {
      showNotification(
        "error",
        "Você não tem permissão para acessar este perfil.",
      );
      navigate("/dashboard", { replace: true });
    }
  }, [user, id, navigate, showNotification]);

  // --- Query para buscar os detalhes do Perfil do Usuário (PADRÃO EditUnitPage) ---
  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
    error: userQueryError,
  } = useQueryWithLoader({
    queryKey: ["userProfile", id],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(entities.users.getById(id));
      if (!response.result) {
        throw new Error(
          response.message || "Dados do usuário não encontrados.",
        );
      }
      return response.result;
    },
    enabled: !!id && user?.id === Number(id),
    staleTime: 5 * 60 * 1000,
  });

  // --- useEffect para carregar os dados do usuário no formulário (PADRÃO EditUnitPage) ---
  useEffect(() => {
    // Só formata os dados se eles foram carregados, não há erro,
    // E se o formData ainda não tem o ID do usuário (para evitar reset)
    if (
      !isLoadingUser &&
      !isErrorUser &&
      !userQueryError &&
      userData &&
      formData.id !== userData.id
    ) {
      formatData(userData, userProfileFields); // Formata os dados no hook useForm
      setFormData(userData); // Define os dados no formData
      hideLoader();
    }

    // Se houver um erro no carregamento da query, exibe notificação e redireciona
    if (isErrorUser && userQueryError) {
      console.error(
        "PerfilUserPage: Erro ao carregar dados do usuário:",
        userQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar perfil: ${
          userQueryError.response?.data?.message || userQueryError.message
        }`,
      );
      hideLoader();
      navigate("/dashboard", {
        replace: true,
        state: {
          type: "error",
          message: "Perfil não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [
    isLoadingUser,
    isErrorUser,
    userQueryError,
    userData,
    formatData,
    hideLoader,
    showNotification,
    navigate,
    formData.id,
  ]);

  // --- Mutação para atualizar o Perfil do Usuário (PADRÃO EditUnitPage) ---
  const updateUserProfileMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.users.update(id),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["userProfile", id] });
        // Navega de volta para o dashboard com uma mensagem de sucesso
        navigate("/dashboard", { state: { type: "success" } });
      }
    },
    onError: (err) => {
      console.error("onError ", err);

      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }

      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const formattedData = removeEmptyValues(formData);
      await updateUserProfileMutation.mutateAsync(formattedData);
    } catch (error) {
      console.error("Erro ao atualizar perfil do usuário:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateUserProfileMutation]);

  const handleBack = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  // --- Efeito para exibir notificações de estado da rota (PADRÃO) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário (PADRÃO) ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return userProfileFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Perfil do Usuário"
        showBackButton={true}
        backUrl="/dashboard"
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Atualizar"
          initialFormData={formData}
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={isLoadingUser || updateUserProfileMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default PerfilUserPage;
